/**
 * The day as this phone knows it, derived from its own outbox (every command it made, with the
 * server's answer once synced). Used for the delivery list, the tag guard's used/void sets, the
 * season light's "today on this phone" and the close-day reconciliation preview (F4.11).
 * No money figures leave this module: lots carry bags and kg only.
 */
import type { OutboxRecord } from '@/offline'
import {
  commitServerState,
  closeServerState,
  type CommitDeliveryPayload,
  type ConfirmRollPayload,
  type OpenSessionPayload,
  type VoidDeliveryPayload,
  type VoidTagPayload,
  type CloseSessionPayload,
  type SpoilSlipPayload,
} from '../commands'
import { currentWeight } from './figures'

export interface DayLot {
  id: string
  code: string
  stream: 'MAIN' | 'CONV'
  bag_count: number
  net_g: number
  hold: string | null
  hold_reasons: string[]
  payout_status: string | null
  utr: string | null
}

export interface DayDelivery {
  command_id: string
  delivery_ulid: string
  kind: 'COMMITTED' | 'VOIDED'
  farmer_id: string
  crop_id: string | null
  void_reason: string | null
  slip_no: string | null
  at: string
  lots: DayLot[]
  /** Outbox status: PENDING / SENDING / APPLIED / HELD / ERROR … */
  sync: OutboxRecord['status']
  exception_ids: string[]
  error: string | null
  disputed: boolean
}

/** Bags in a lot: the typed count, else (V1, nothing typed) its tagged bags. */
function lotBags(p: CommitDeliveryPayload, l: CommitDeliveryPayload['lots'][number]): number {
  return l.bag_count ?? p.bags.filter((b) => b.unit_id === l.id && !b.withdrawn).length
}

function lotNet(p: CommitDeliveryPayload, lotId: string): number {
  return p.bags
    .filter((b) => b.unit_id === lotId && !b.withdrawn)
    .reduce((sum, b) => sum + (currentWeight(b.weights)?.gross_g ?? 0) - b.tare_g, 0)
}

export function deliveriesOf(records: readonly OutboxRecord[], sessionId: string): DayDelivery[] {
  const out: DayDelivery[] = []
  for (const r of records) {
    if (r.session_id !== sessionId) continue
    if (r.type === 'commit_delivery') {
      const p = r.payload as CommitDeliveryPayload
      const st = commitServerState.safeParse(r.result?.server_state ?? {})
      const server = st.success ? st.data.lots : []
      out.push({
        command_id: r.id,
        delivery_ulid: p.delivery_ulid,
        kind: 'COMMITTED',
        farmer_id: p.farmer_id,
        crop_id: p.crop_id,
        void_reason: null,
        slip_no: p.slip.slip_no,
        at: p.closed_at,
        disputed: p.slip.disputed,
        lots: p.lots.map((l) => {
          const s = server.find((x) => x.id === l.id)
          return {
            id: l.id,
            code: l.code,
            stream: l.stream,
            bag_count: lotBags(p, l),
            net_g: lotNet(p, l.id),
            hold: s?.hold ?? null,
            hold_reasons: s?.hold_reasons ?? (s?.hold_reason ? s.hold_reason.split(',') : []),
            payout_status: s?.payout_status ?? null,
            utr: s?.utr ?? null,
          }
        }),
        sync: r.status,
        exception_ids: r.result?.exception_ids ?? [],
        error: r.last_error,
      })
    } else if (r.type === 'void_delivery') {
      const p = r.payload as VoidDeliveryPayload
      out.push({
        command_id: r.id,
        delivery_ulid: p.delivery_ulid,
        kind: 'VOIDED',
        farmer_id: p.farmer_id,
        crop_id: p.crop_id,
        void_reason: p.reason,
        slip_no: null,
        at: p.voided_at,
        disputed: false,
        lots: p.lots.map((l) => ({
          id: l.id,
          code: l.code,
          stream: l.stream,
          bag_count: 0,
          net_g: 0,
          hold: null,
          hold_reasons: [],
          payout_status: null,
          utr: null,
        })),
        sync: r.status,
        exception_ids: r.result?.exception_ids ?? [],
        error: r.last_error,
      })
    }
  }
  return out.sort((a, b) => (a.at < b.at ? 1 : -1))
}

/** Codes bound / voided by this phone (all sessions) — for the tag guard. */
export function usedCodes(records: readonly OutboxRecord[]): {
  bound: Set<string>
  voided: Set<string>
} {
  const bound = new Set<string>()
  const voided = new Set<string>()
  for (const r of records) {
    if (r.status === 'ERROR' && r.last_error_code === 'REJECTED_MALFORMED') continue
    if (r.type === 'commit_delivery') {
      const p = r.payload as CommitDeliveryPayload
      p.bags.forEach((b) => (b.withdrawn ? voided : bound).add(b.qr_payload))
      if (p.sample) bound.add(p.sample.seal_payload)
      p.voided_tags.forEach((v) => voided.add(v.qr_payload))
    } else if (r.type === 'void_delivery') {
      const p = r.payload as VoidDeliveryPayload
      p.voided_tags.forEach((v) => voided.add(v.qr_payload))
      if (p.voided_seal) voided.add(p.voided_seal.qr_payload)
    } else if (r.type === 'void_tag') {
      voided.add((r.payload as VoidTagPayload).qr_payload)
    } else if (r.type === 'retag') {
      bound.add((r.payload as { qr_payload: string }).qr_payload)
    }
  }
  for (const v of voided) bound.delete(v)
  return { bound, voided }
}

/** MAIN net grams per farmer+crop committed on this phone after `since` (season light). */
export function mainNetSince(
  records: readonly OutboxRecord[],
  farmerId: string,
  cropId: string,
  since: number,
): number {
  let g = 0
  for (const r of records) {
    if (r.type !== 'commit_delivery' || r.created_at < since) continue
    const p = r.payload as CommitDeliveryPayload
    if (p.farmer_id !== farmerId || p.crop_id !== cropId) continue
    for (const l of p.lots) if (l.stream === 'MAIN') g += lotNet(p, l.id)
  }
  return g
}

export interface Reconciliation {
  tags: {
    atOpen: number
    confirmedToday: number
    bound: number
    voided: number
    shouldBeLeft: number
  }
  seals: {
    atOpen: number
    confirmedToday: number
    bound: number
    voided: number
    shouldBeLeft: number
  }
  lotsMain: number
  lotsConv: number
  bagsMain: number
  bagsConv: number
  netMainG: number
  netConvG: number
  commandCount: number
}

/**
 * F4.11 preview from local data: in hand at open + rolls confirmed today = on bags + voided +
 * counted back — tags and seals separately. The server decides from the diary.
 */
export function reconcile(records: readonly OutboxRecord[], sessionId: string): Reconciliation {
  const rec: Reconciliation = {
    tags: { atOpen: 0, confirmedToday: 0, bound: 0, voided: 0, shouldBeLeft: 0 },
    seals: { atOpen: 0, confirmedToday: 0, bound: 0, voided: 0, shouldBeLeft: 0 },
    lotsMain: 0,
    lotsConv: 0,
    bagsMain: 0,
    bagsConv: 0,
    netMainG: 0,
    netConvG: 0,
    commandCount: 0,
  }
  let openRollIds: string[] = []
  for (const r of records) {
    if (r.session_id !== sessionId) continue
    if (r.type === 'open_session') {
      const p = r.payload as OpenSessionPayload
      rec.tags.atOpen = p.tags_in_hand
      rec.seals.atOpen = p.seals_in_hand
      openRollIds = [p.tag_batch_id, p.seal_batch_id].filter((x): x is string => !!x)
    }
  }
  for (const r of records) {
    if (r.session_id !== sessionId) continue
    if (r.type !== 'close_session') rec.commandCount++
    const kindOf = (k: 'BAG' | 'SEAL') => (k === 'BAG' ? rec.tags : rec.seals)
    switch (r.type) {
      case 'confirm_roll': {
        const p = r.payload as ConfirmRollPayload
        // The morning roll and strip are "in hand at open"; later ones are opened today.
        const morning = openRollIds.includes(p.batch_id) && isOpening(records, r)
        if (!morning) kindOf(p.kind).confirmedToday += p.tags_in_roll
        break
      }
      case 'commit_delivery': {
        const p = r.payload as CommitDeliveryPayload
        for (const b of p.bags) if (!b.withdrawn) rec.tags.bound++
        if (p.sample) rec.seals.bound++
        for (const v of p.voided_tags) kindOf(v.kind).voided++
        for (const l of p.lots) {
          const net = lotNet(p, l.id)
          if (l.stream === 'MAIN') {
            rec.lotsMain++
            rec.bagsMain += lotBags(p, l)
            rec.netMainG += net
          } else {
            rec.lotsConv++
            rec.bagsConv += lotBags(p, l)
            rec.netConvG += net
          }
        }
        break
      }
      case 'void_delivery': {
        const p = r.payload as VoidDeliveryPayload
        for (const v of p.voided_tags) kindOf(v.kind).voided++
        if (p.voided_seal) rec.seals.voided++
        break
      }
      case 'void_tag':
        kindOf((r.payload as VoidTagPayload).kind).voided++
        break
    }
  }
  for (const k of [rec.tags, rec.seals])
    k.shouldBeLeft = k.atOpen + k.confirmedToday - k.bound - k.voided
  return rec
}

/** A confirm_roll enqueued in the same batch as open_session (made together at Open day). */
function isOpening(records: readonly OutboxRecord[], r: OutboxRecord): boolean {
  const open = records.find((x) => x.type === 'open_session' && x.session_id === r.session_id)
  if (!open) return false
  return Math.abs(r.created_at - open.created_at) < 5_000 && r.parent_ids.includes(open.id)
}

export function closeResult(records: readonly OutboxRecord[], sessionId: string) {
  const r = records.find((x) => x.type === 'close_session' && x.session_id === sessionId)
  if (!r) return null
  const st = closeServerState.safeParse(r.result?.server_state ?? {})
  return {
    status: r.status,
    payload: r.payload as CloseSessionPayload,
    balanced: st.success ? (st.data.balanced ?? null) : null,
    exception_id: st.success ? (st.data.exception_id ?? null) : null,
    acked_at: r.acked_at,
  }
}

export function spoiledSlips(records: readonly OutboxRecord[], sessionId: string): string[] {
  return records
    .filter((r) => r.type === 'spoil_slip' && r.session_id === sessionId)
    .map((r) => (r.payload as SpoilSlipPayload).receipt_slip.slip_no)
}

/**
 * F4-B3.b: slip numbers between the first and last used that are neither typed in nor spoiled.
 * Numbers compare as integers; returned with the book's zero padding of the first used slip.
 */
export function missingSlips(used: readonly string[]): string[] {
  if (used.length === 0) return []
  const nums = used.map(Number)
  const min = Math.min(...nums)
  const max = Math.max(...nums)
  const have = new Set(nums)
  const width = used[0]!.length
  const out: string[] = []
  for (let n = min; n <= max; n++) if (!have.has(n)) out.push(String(n).padStart(width, '0'))
  return out
}
